import React, { Fragment, useContext, useEffect, useState } from "react";
import moment from "moment";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FaTrashAlt } from "react-icons/fa";
import { OrderContext } from "./index";
import { fetchData, editOrderReq, deleteOrderReq } from "./Actions";

const apiURL = process.env.REACT_APP_API_URL;

// Format Rupiah
const formatRupiah = (amount) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(amount);

const AllCategory = () => {
  const { data, dispatch } = useContext(OrderContext);
  const { orders, loading } = data;

  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 5;

  useEffect(() => {
    fetchData(dispatch);
  }, [dispatch]);

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("Order Report", doc.internal.pageSize.getWidth() / 2, 14, { align: "center" });

    const tableColumn = [
      "No",
      "Products",
      "Status",
      "Total",
      "Payment",
      "Transaction ID",
      "Customer",
      "Email",
      "Phone",
      "Address",
      "Created",
      "Updated"
    ];
    const tableRows = [];

    orders.forEach((order, index) => {
      const products = order.allProduct
        .map((p) => `${p.id?.pName || "Unknown"} (${p.quantity}x)`).join(", ");
      const row = [
        index + 1,
        products,
        order.status,
        formatRupiah(order.amount),
        order.paymentMethod,
        order.transactionId,
        order.user?.name,
        order.user?.email,
        order.phone,
        order.address,
        moment(order.createdAt).format("lll"),
        moment(order.updatedAt).format("lll")
      ];
      tableRows.push(row);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 8 },
      startY: 20
    });

    doc.save("order_report.pdf");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <svg className="w-12 h-12 animate-spin text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </div>
    );
  }

  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = orders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(orders.length / ordersPerPage);

  return (
    <Fragment>
      <div className="col-span-1 overflow-auto bg-white shadow-lg p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">All Orders</h2>
          <button
            onClick={exportPDF}
            className="bg-yellow-700 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Export PDF
          </button>
        </div>
        <table className="table-auto border w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 border text-center">No</th>
              <th className="px-12 py-2 border text-center">Products</th>
              <th className="px-10 py-2 border text-center">Status</th>
              <th className="px-4 py-2 border text-center">Total</th>
              <th className="px-4 py-2 border text-center">Payment Method</th>
              <th className="px-12 py-2 border text-center">Transaction Id</th>
              <th className="px-4 py-2 border text-center">Customer</th>
              <th className="px-4 py-2 border text-center">Email</th>
              <th className="px-4 py-2 border text-center">Phone</th>
              <th className="px-12 py-2 border text-center">Address</th>
              <th className="px-4 py-2 border text-center">Created at</th>
              <th className="px-4 py-2 border text-center">Updated at</th>
              <th className="px-4 py-2 border text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentOrders.length > 0 ? (
              currentOrders.map((item, i) => (
                <CategoryTable
                  key={i}
                  index={indexOfFirstOrder + i + 1}
                  order={item}
                  editOrder={(oId, type, status) =>
                    editOrderReq(oId, type, status, dispatch)
                  }
                />
              ))
            ) : (
              <tr>
                <td colSpan="13" className="text-center py-8 text-gray-600">
                  No order found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex justify-center mt-4 space-x-2">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`w-8 h-8 rounded-full text-sm font-medium flex items-center justify-center ${
                currentPage === i + 1
                  ? "bg-yellow-700 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>

        <div className="text-sm text-gray-600 mt-2">
          Total {orders.length} order(s) found
        </div>
      </div>
    </Fragment>
  );
};

const CategoryTable = ({ index, order, editOrder }) => {
  const { dispatch } = useContext(OrderContext);

  return (
    <Fragment>
      <tr className="border-b text-center">
        <td className="p-2">{index}</td>
        <td className="p-2 text-sm text-gray-800">
          {order.allProduct.map((product, i) => (
            <div key={i} className="flex items-center justify-center space-x-2 mb-1">
              <img
                className="w-8 h-8 object-cover object-center rounded"
                src={`${apiURL}/uploads/products/${product.id?.pImages?.[0]}`}
                alt="productImage"
              />
              <span>{product.id?.pName || "Unknown Product"}</span>
              <span>{product.quantity}x</span>
            </div>
          ))}
        </td>
        <td className="p-2">
          <span className={`block rounded-full text-center text-xs px-2 font-semibold ${
            order.status === "Not processed" ? "text-red-600" :
            order.status === "Processing" ? "text-yellow-600" :
            order.status === "Shipped" ? "text-blue-600" :
            order.status === "Delivered" ? "text-green-600" :
            order.status === "Ready to Pick up at the store" ? "text-green-600" : "text-green-500"
          }`}>
            {order.status}
          </span>
        </td>
        <td className="p-2">{formatRupiah(order.amount)}</td>
        <td className="p-2">{order.paymentMethod || "Unknown"}</td>
        <td className="p-2">{order.transactionId}</td>
        <td className="p-2">{order.user?.name || "Unknown"}</td>
        <td className="p-2">{order.user?.email || "-"}</td>
        <td className="p-2">{order.phone}</td>
        <td className="p-2">{order.address}</td>
        <td className="p-2">{moment(order.createdAt).format("lll")}</td>
        <td className="p-2">{moment(order.updatedAt).format("lll")}</td>
        <td className="p-2">
          <div className="flex justify-center space-x-2">
            <span
              onClick={() => editOrder(order._id, true, order.status)}
              className="cursor-pointer hover:bg-gray-200 rounded-lg p-2"
              title="Edit"
            >
              ✏️
            </span>
            <span
              onClick={() => deleteOrderReq(order._id, dispatch)}
              className="cursor-pointer text-red-600 hover:text-red-800 hover:bg-gray-200 rounded-lg p-2"
              title="Delete"
            >
              <FaTrashAlt size={17} />
            </span>
          </div>
        </td>
      </tr>
    </Fragment>
  );
};

export default AllCategory;


